import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import ProductProvider from '@bluheadless/ui-logic/src/providers/product'
import { oneOf } from 'prop-types'
//import TileBase from '../base'
import { usePrevious } from '@bluheadless/ui-logic/src/hooks/usePrevious'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import TileBridge from './tile-bridge'
import { variants } from './tile-variants'

const TileConnected = memo(
	function TileConnected({
		variant,
		product,
		formDefaultValues,
		propagateContext,
		aroundAddToCart,
		aroundRemoveFromCart,
		aroundCartItemUpdate,
		aroundCartIncrementQty,
		aroundCartDecrementQty,
		aroundAddToWishList,
		aroundRemoveFromWishList,
		aroundWishListIncrementQty,
		aroundWishListDecrementQty,
		aroundMoveFromWishListToCart,
		aroundWishListItemUpdate,
		validationMode,
		defaultQty,
		...props
	}) {
		const {
			cart: { backOrderChip },
			catalog: {
				products: {
					outOfStockChip,
					additionalLabel: { attributeCode, color, textColor, colorAttributeCode, textColorAttributeCode },
				},
			},
		} = useConfig()

		const { formatMessage } = useIntl()

		const [currentProduct, setCurrentProduct] = useState(product)

		const {
			inventory: { allowBackOrders, inStock },
			attributes,
		} = currentProduct ?? {}

		const [currentConfigurableChildrenProductsFallback, setCurrentConfigurableChildrenProductsFallback] = useState()
		const [currentGroupedChildrenProductsFallback, setCurrentGroupedChildrenProductsFallback] = useState()
		const [currentBundleChildrenProductsFallback, setCurrentBundleChildrenProductsFallback] = useState()

		const onUpdateProduct = useCallback(
			({
				product,
				configurableChildrenProductsFallback,
				groupedChildrenProductsFallback,
				bundleChildrenProductsFallback,
			}) => {
				setCurrentProduct(product)
				setCurrentConfigurableChildrenProductsFallback(configurableChildrenProductsFallback)
				setCurrentGroupedChildrenProductsFallback(groupedChildrenProductsFallback)
				setCurrentBundleChildrenProductsFallback(bundleChildrenProductsFallback)
			},
			[]
		)

		const additionalLabel = attributes?.[attributeCode]?.value
		const additionalLabelColor = attributes?.[colorAttributeCode]?.value || color
		const additionalLabelTextColor = attributes?.[textColorAttributeCode]?.value || textColor

		const additionalLabelsProps = useMemo(() => {
			const labels = []

			if (!inStock) {
				labels.push({
					label: formatMessage({ id: 'out_of_stock' }),
					color: outOfStockChip?.color,
					textColor: outOfStockChip?.textColor,
					id: 'out_of_stock',
				})
			}

			if (allowBackOrders) {
				labels.push({
					label: formatMessage({ id: 'back_order' }),
					color: backOrderChip?.color,
					textColor: backOrderChip?.textColor,
					id: 'back_order',
				})
			}

			if (additionalLabel) {
				labels.push({
					label: additionalLabel,
					color: additionalLabelColor,
					textColor: additionalLabelTextColor,
					id: 'product_label',
				})
			}

			return {
				position: 'left',
				labels,
			}
		}, [
			inStock,
			allowBackOrders,
			additionalLabel,
			additionalLabelColor,
			additionalLabelTextColor,
			formatMessage,
			outOfStockChip?.color,
			outOfStockChip?.textColor,
			backOrderChip?.color,
			backOrderChip?.textColor,
		])

		const previousProductId = usePrevious(product?.id)

		useEffect(() => {
			if (product?.id !== previousProductId) {
				// update current product when product id changes
				// otherwise the provider will not be updated when the product prop changes
				setCurrentProduct(product)
			}
		}, [product, previousProductId])

		return (
			<ProductProvider
				product={currentProduct}
				configurableChildrenProductsFallback={currentConfigurableChildrenProductsFallback}
				groupedChildrenProductsFallback={currentGroupedChildrenProductsFallback}
				bundleChildrenProductsFallback={currentBundleChildrenProductsFallback}
				formDefaultValues={formDefaultValues}
				propagateContext={propagateContext}
				validationMode={validationMode}
				aroundAddToCart={aroundAddToCart}
				aroundRemoveFromCart={aroundRemoveFromCart}
				aroundCartItemUpdate={aroundCartItemUpdate}
				aroundCartIncrementQty={aroundCartIncrementQty}
				aroundCartDecrementQty={aroundCartDecrementQty}
				aroundAddToWishList={aroundAddToWishList}
				aroundRemoveFromWishList={aroundRemoveFromWishList}
				aroundWishListIncrementQty={aroundWishListIncrementQty}
				aroundWishListDecrementQty={aroundWishListDecrementQty}
				aroundMoveFromWishListToCart={aroundMoveFromWishListToCart}
				aroundWishListItemUpdate={aroundWishListItemUpdate}
				defaultQty={defaultQty}
				pageContext={props?.pageContext}
				pageId={props?.pageId}
				widgetContext={props?.widgetContext}
			>
				<TileBridge
					additionalLabelsProps={additionalLabelsProps}
					variant={variant}
					Tile={variants[variant]}
					onUpdateProduct={onUpdateProduct}
					{...props}
				/>
			</ProductProvider>
		)
	},
	(prevProps, nextProps) => {
		return (
			prevProps.product?.id === nextProps.product?.id &&
			prevProps.variant === nextProps.variant &&
			prevProps.modeView === nextProps.modeView
		)
	}
)

TileConnected.defaultProps = { variant: 'base' }

TileConnected.propTypes = {
	//...TileBase.propTypes,
	variant: oneOf(Object.keys(variants)).isRequired,
}

export default TileConnected
